import SQL from './dbOverviewSql.json';

/**
 * What the database overview shows, per engine: which sections exist (only
 * the ones the engine has — Events are MySQL's, Sequences Postgres's), and how
 * each catalog column is labelled and formatted.
 *
 * The SQL lives in dbOverviewSql.json (the e2e check, e2e/demo/dbOverview.check.js,
 * runs it against real servers through the app); this file only decides presentation.
 */

/** Canonical order of the left-hand section list (DBeaver's order). */
export const SECTION_ORDER = ['tables', 'views', 'indexes', 'routines', 'sequences', 'triggers', 'events'];

/** Sections that list something with table DATA — Enter / double-click opens it. */
export const OPENABLE_SECTIONS = new Set(['tables', 'views']);
/** Sections whose rows belong to a table (open that table instead). */
export const TABLE_OWNED_SECTIONS = new Set(['indexes', 'triggers']);

/**
 * How a column's values are drawn and compared.
 *  - text:     as is
 *  - number:   grouped digits, numeric sort
 *  - estimate: like number, but the catalog only guesses (≈ in the header)
 *  - bytes:    1.2 MB, numeric sort on the byte count
 *  - bool:     ✓ or nothing
 */
export const COLUMN_KIND = {
  rows: 'estimate',
  dataSize: 'bytes',
  indexSize: 'bytes',
  totalSize: 'bytes',
  size: 'bytes',
  autoIncrement: 'number',
  columnCount: 'number',
  start: 'number',
  increment: 'number',
  lastValue: 'number',
  max: 'number',
  partitioned: 'bool',
  unique: 'bool',
  primary: 'bool',
  enabled: 'bool',
  updatable: 'bool',
  withoutRowid: 'bool',
  strict: 'bool',
};

export function columnKind(key) {
  return COLUMN_KIND[key] || 'text';
}

/**
 * Display order when it differs from SELECT order — SQLite's table sizes and
 * row estimates come from optional side queries, so the main statement cannot
 * place them.
 */
const DISPLAY_ORDER = {
  sqlite: { tables: ['name', 'rows', 'dataSize', 'indexSize', 'columnCount', 'withoutRowid', 'strict'] },
};

export function catalogFor(engine) {
  return SQL[engine] || null;
}

/** The sections this engine has, in canonical order. */
export function sectionsFor(engine) {
  const spec = catalogFor(engine);
  if (!spec) return [];
  return SECTION_ORDER.filter((id) => spec.sections[id]);
}

/** Column keys drawn for one section. */
export function displayColumns(engine, sectionId, { showSchema = true } = {}) {
  const spec = catalogFor(engine)?.sections?.[sectionId];
  if (!spec) return [];
  const ordered = DISPLAY_ORDER[engine]?.[sectionId];
  let cols = ordered || [...spec.columns, ...(spec.extras || []).flatMap((x) => x.columns.filter((c) => c !== 'name'))];
  if (!showSchema) cols = cols.filter((c) => c !== 'schema');
  // Name first, always: it is what the row IS (and what the filter matches).
  return ['name', ...cols.filter((c) => c !== 'name')];
}

const NUMERIC_RE = /^-?\d+(\.\d+)?$/;

/** A catalog number, whichever way the driver sent it (bigint past 2^53 is a string). */
export function toNumber(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'bigint') return Number(v);
  const s = String(v).trim();
  return NUMERIC_RE.test(s) ? Number(s) : null;
}

/** Booleans arrive as true/false (Postgres), 1/0 (MySQL, SQLite) or YES/NO (information_schema). */
export function toBool(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v === null || v === undefined) return false;
  const s = String(v).trim().toLowerCase();
  return s === '1' || s === 't' || s === 'true' || s === 'yes';
}

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/** 1 KB = 1024 B, like every database tool (DBeaver, DataGrip, pgAdmin). */
export function formatBytes(n) {
  const v = toNumber(n);
  if (v === null) return '';
  let x = v;
  let u = 0;
  while (x >= 1024 && u < UNITS.length - 1) {
    x /= 1024;
    u += 1;
  }
  return u === 0 ? `${x} B` : `${x.toFixed(x >= 100 ? 0 : 1)} ${UNITS[u]}`;
}

export function formatCount(n, locale) {
  const v = toNumber(n);
  if (v === null) return n === null || n === undefined ? '' : String(n);
  return v.toLocaleString(locale);
}

/** One cell as drawn. */
export function formatCell(key, value, { locale, yes = '✓' } = {}) {
  const kind = columnKind(key);
  if (kind === 'bytes') return formatBytes(value);
  if (kind === 'number') return formatCount(value, locale);
  if (kind === 'estimate') {
    const v = toNumber(value);
    return v === null ? '' : `≈ ${v.toLocaleString(locale)}`;
  }
  if (kind === 'bool') return toBool(value) ? yes : '';
  if (value === null || value === undefined) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/** One cell as compared when sorting. */
export function sortValue(key, value) {
  const kind = columnKind(key);
  if (kind === 'bool') return toBool(value) ? 1 : 0;
  if (kind === 'text') return value === null || value === undefined ? '' : String(value).toLowerCase();
  return toNumber(value);
}
